<script lang="ts" setup>
import type { Transcription } from '../logic'
import { breakpointsTailwind } from '@vueuse/core'
import { useAudioPlayer, useComments } from '../logic'

const props = defineProps<{
  tag?: string
  data: Transcription.IContentParagraph

  canEdit?: boolean
  onEditSpeakerName?: VoidFunction
}>()

const id = useId()
const breakpoints = useBreakpoints(breakpointsTailwind)

const comments = useComments()
const entries = computed(() =>
  comments.list.filter(
    (entry) => {
      if (props.data.index === 0 && entry.seconds <= props.data.start)
        return true

      return entry.seconds >= props.data.start && entry.seconds < props.data.end
    }
  )
)

function updatePlayerTime(seconds: number) {
  const { audio } = useAudioPlayer()
  audio.currentTime = seconds
}

function getStackedCardStyle(length: number, index: number) {
  const offset = Math.min(6, (length - index - 1) * 3)

  return {
    top: `${index * 30}px`,
    width: `${100 - offset}%`,
    marginLeft: `${offset / 2}%`
  }
}

function editSpeakerName() {
  if (props.canEdit)
    props.onEditSpeakerName?.()
}
</script>

<template>
  <component
    :is="tag ?? 'p'" class="relative min-w-full xl:min-h-$sidebar-min-height"
    :style="{ '--sidebar-min-height': `${entries.length * 43}px` }"
  >
    <ClientOnly>
      <div v-if="data.speaker" :title="data.speaker" tabindex="0" class="smooth-size group hover:px-2 hover:w-max hover:opacity-100 mr-2 size-24px flex-center flex-inline align-middle opacity-60 transition-all whitespace-nowrap cursor-pointer bg-$color-neutral text-xs text-$color-background rounded-full sm:absolute sm:top-2px sm:left-0 sm:-ml-4 sm:transform sm:-translate-x-full">
        <span class="group-hover:hidden">{{ data.speaker[0] }}</span>

        <button class="group-not-hover:hidden gap-1 flex items-center" @click="editSpeakerName">
          {{ data.speaker }}
          <span v-if="canEdit" class="i-solar:pen-bold size-3" />
        </button>
      </div>
    </ClientOnly>

    <slot />

    <ClientOnly>
      <section
        v-if="comments.showDesktopSidebar && breakpoints.xl.value"
        class="w-240px absolute h-full top-0 left-full ml-10 [&:has(.active),&:hover]:z-10 animate-in animate-ease animate-duration-300 fade-in slide-in-b-5"
        :style="{ viewTransitionName: `desktop-comments-${id}` }"
      >
        <LazyPageListenCommentsCard
          v-for="(comment, i) in entries" :key="i" :data="comment"
          class="group comment absolute p-3 rounded-md transition-all bg-$color-background-elevated outline-1 outline-solid outline-transparent cursor-pointer ![&:hover,&:active,&.active]:z-1 ![&:hover,&:active,&.active]:w-full ![&:hover,&:active,&.active]:m-0 ![&:hover,&:active,&.active]:outline-$color-primary"
          :class="{ active: comments.highlightedList.includes(comment) }"
          message-class="group-not-hover:line-clamp-3 group-hover:-mx-3 group-hover:px-3 group-hover:max-h-20vh group-hover:overflow-y-auto"
          :date-class="{ 'group-not-hover:hidden': i !== entries.length - 1 }"
          :style="getStackedCardStyle(entries.length, i)" @click="updatePlayerTime(comment.seconds)"
        />
      </section>
    </ClientOnly>
  </component>
</template>

<style scoped lang="sass">
.smooth-size
  interpolate-size: allow-keywords

.comment
  interpolate-size: allow-keywords
  box-shadow: 0 4px 8px var(--color-neutral-5), 0 -2px 6px rgba(0,0,0,0.05)
</style>
